import { useEffect, useState, DependencyList, useCallback, useRef } from 'react';

import { createUID } from '../utils';

export interface UseActiveDescendantConfig {
  /** DOM element which controls and has active descendants */
  focusEl: HTMLElement | null;
  /** Root scope for descendant options */
  scope: HTMLElement | null;
  /** Custom selector for specific descendant scope in root descendant scope */
  scopeSelector?: string;
  /** Custom selector for descendant options */
  selector?: string;
  /** One-time override for the focused descendant, triggers re-evaluation.
   * Include `clearFocusDescendantEl` for cleanup.
   */
  focusDescendantEl?: HTMLElement | null;
  /** Callback to clear focusDescendantEl after use. */
  clearFocusDescendant?: () => void;
  /** One-time override for focused element on NEXT re-evaluation of active descendants.
   * Include `clearFocusReturn` for cleanup.
   */
  focusReturnEl?: HTMLElement | null;
  /** Callback to clear focusReturnEl after use. */
  clearFocusReturn?: () => void;
  /**
   * Descendant ID used for continuous override of current active descendant,
   * used for external focus control / keyDown bindings (see RTE).
   * NOTE: setting currentDescendantId once effectively disables the keybindings of this hook.
   * If you only need to set the current element once, use focusReturnEl & clearFocusReturn.
   * @deprecated
   */
  currentDescendantId?: string;
  /** Explicit onClick handler called on keydown 'enter' event */
  onClick?: (currentDescendantEl: HTMLElement) => void;
  /** Prevent scrolling to the active descendant on the initial render.
   * Include `preventInitialScroll` for cleanup.
   */
  preventInitialScroll?: boolean;
  /** Callback to clear preventInitialScroll after use. */
  clearPreventScroll?: () => void;
  /** Boolean to prevent descendant evaluation.
   * @default false
   */
  pauseDescendantEvaluation?: boolean;
}

type CurrentDescendant = HTMLElement | undefined;
type Descendants = HTMLElement[] | null;

const useActiveDescendant = (
  {
    focusEl,
    scope,
    scopeSelector,
    selector,
    focusDescendantEl,
    clearFocusDescendant,
    focusReturnEl,
    clearFocusReturn,
    currentDescendantId,
    onClick,
    preventInitialScroll,
    pauseDescendantEvaluation = false,
    clearPreventScroll
  }: UseActiveDescendantConfig,
  dependencyArray: DependencyList = []
): {
  activeDescendant: CurrentDescendant;
  descendants: Descendants;
} => {
  const [resetId, setResetId] = useState(0);
  const previousActiveId = useRef('');
  const [focusDescendantElIndex, setFocusDescendantElIndex] = useState<number | null>(null);
  const [currentIndex, setCurrentIndex] = useState<number | null>(null);
  const [descendants, setDescendants] = useState<Descendants>();

  const clearThenSetDescendants = useCallback(
    (setVal: NodeListOf<Element> | null) => {
      descendants?.forEach(node => {
        node.setAttribute('data-current', 'false');
      });

      setDescendants(
        setVal
          ? Array.from(setVal).filter((item): item is HTMLElement => item instanceof HTMLElement)
          : null
      );
    },
    [descendants]
  );

  // ## Toggle active scope data attr.
  useEffect(() => {
    if (!scope || !focusEl) return;

    scope.setAttribute('data-active-scope', document.activeElement === focusEl ? 'true' : 'false');

    const onFocus = () => {
      scope.setAttribute('data-active-scope', 'true');
    };
    const onBlur = () => {
      scope.setAttribute('data-active-scope', 'false');
    };

    focusEl.addEventListener('focus', onFocus);
    focusEl.addEventListener('blur', onBlur);

    return () => {
      focusEl.removeEventListener('focus', onFocus);
      focusEl.removeEventListener('blur', onBlur);
    };
  }, [scope, focusEl]);

  // ## Update descendants
  useEffect(() => {
    if (!pauseDescendantEvaluation) {
      // 0 second timeout added because descendantScope needs to be up to date before running query after dependencyArray change
      setTimeout(() => {
        let hasScope = scope;

        if (hasScope && hasScope instanceof HTMLElement) {
          if (scopeSelector) {
            hasScope = hasScope.querySelector(scopeSelector);
          }

          if (!hasScope) {
            clearThenSetDescendants(null);
            return;
          }

          if (selector) {
            clearThenSetDescendants(hasScope.querySelectorAll(selector));
          } else {
            clearThenSetDescendants(
              hasScope.querySelectorAll(
                'a, button, input, textarea, select, details, [tabindex]:not([tabindex="-1"])'
              )
            );
          }
        } else {
          clearThenSetDescendants(null);
        }
      }, 0);
    }
  }, [pauseDescendantEvaluation, scope, scopeSelector, selector, focusEl, ...dependencyArray]);

  // ## Set IDs and aria-owns
  useEffect(() => {
    const ownedIds: string[] = [];
    setCurrentIndex(0);

    if (descendants && descendants.length) {
      descendants.forEach(node => {
        node.id = node.id || createUID();
        ownedIds.push(node.id);
      });
    }

    focusEl?.setAttribute('aria-owns', ownedIds.join(' '));

    return () => {
      focusEl?.removeAttribute('aria-owns');
    };
  }, [focusEl, descendants]);

  // ## Bind focus el keyDown
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (descendants && descendants.length) {
        switch (e.key) {
          case 'ArrowDown':
            e.preventDefault();
            // Focus next or first
            clearFocusReturn?.();
            clearPreventScroll?.();
            if (currentIndex !== null && currentIndex + 1 < descendants.length) {
              setCurrentIndex(currentIndex + 1);
            } else {
              setCurrentIndex(0);
            }
            break;
          case 'ArrowUp':
            e.preventDefault();
            // Focus previous or last
            clearFocusReturn?.();
            clearPreventScroll?.();
            if (currentIndex !== null && currentIndex - 1 > -1) {
              setCurrentIndex(currentIndex - 1);
            } else {
              setCurrentIndex(descendants.length - 1);
            }
            break;
          case 'Enter':
            e.preventDefault();
            // Click focused item
            if (descendants && descendants.length && currentIndex !== null) {
              if (onClick) {
                onClick(descendants[currentIndex]);
                break;
              }

              const nodeName = descendants[currentIndex].nodeName.toLowerCase();
              if (nodeName === 'input' || nodeName === 'button' || nodeName === 'a') {
                descendants[currentIndex].click();
              } else {
                descendants[currentIndex].querySelector<HTMLElement>('button, a, input')?.click();
              }
            }
            break;
          default:
            break;
        }
      }
    };

    // Do not rebind once / if `currentDescendantId` control is defined
    if (focusEl && descendants?.length && !currentDescendantId) {
      focusEl.addEventListener('keydown', onKeyDown);
    }

    return () => {
      focusEl?.removeEventListener('keydown', onKeyDown);
    };
  }, [focusEl, currentIndex, descendants]);

  // ## Handle independent control update
  useEffect(() => {
    if (descendants && currentDescendantId) {
      descendants.forEach((node, index) => {
        if (node.id === currentDescendantId) {
          setCurrentIndex(index);
        }
      });
    }
  }, [currentDescendantId, descendants]);

  // ## Set and scroll to current descendant
  useEffect(() => {
    const nextIndex = focusDescendantElIndex || currentIndex;
    const focusReturnId = focusReturnEl?.id;
    let focusReturnIndex;
    const focusDescendantElId = focusDescendantEl?.id;
    let focusDescendantIndex;
    let foundFocusDescendantEl = false;

    if (descendants && descendants.length) {
      // clear previous & catch focusReturn / focusDescendantEl index
      descendants.forEach((node, index) => {
        if (focusDescendantElIndex === null && node.id === focusDescendantElId) {
          focusDescendantIndex = index;
          foundFocusDescendantEl = true;
          setFocusDescendantElIndex(index);
        }
        if (node.id === focusReturnId) {
          focusReturnIndex = index;
        }
        node.setAttribute('data-current', 'false');
      });

      // early return for focusReturn / focusDescendantEl
      if (focusReturnIndex && focusReturnIndex !== currentIndex) {
        setCurrentIndex(focusReturnIndex);
        clearFocusReturn?.();
        return;
      }
      if (foundFocusDescendantEl && focusDescendantIndex !== undefined) {
        // hard reset to continue focusDescendantEl process even if it is the active descendant
        setResetId(Math.random());
        setCurrentIndex(focusDescendantIndex);
        return;
      }

      // set new
      if (nextIndex !== null && descendants[nextIndex]) {
        const itemEl = descendants[nextIndex!];
        itemEl.setAttribute('data-current', 'true');
        focusEl?.setAttribute('aria-activedescendant', itemEl.id);

        // scroll to element
        if (itemEl.id !== previousActiveId.current && !preventInitialScroll) {
          const scrollTo: (this: Element, arg: boolean) => void =
            Element.prototype.scrollIntoViewIfNeeded ?? Element.prototype.scrollIntoView;

          scrollTo?.call(itemEl, false);
        }

        // focusDescendantEl cleanup
        if (focusDescendantElIndex !== null) {
          setFocusDescendantElIndex(null);
          clearFocusDescendant?.();
        }

        previousActiveId.current = itemEl.id;
      }
    }

    return () => {
      focusEl?.removeAttribute('aria-activedescendant');
    };
  }, [descendants, currentIndex, focusDescendantEl, focusEl, resetId]);

  return {
    activeDescendant: currentIndex !== null && descendants ? descendants[currentIndex] : undefined,
    descendants: descendants || null
  };
};

export default useActiveDescendant;
